import { createTheme } from '@mui/material/styles';

// TrustLab brand color palette - matching the design reference
export const colors = {
  forest: '#152A1E',
  forestMid: '#1E3D2A',
  forestLight: '#264D36',
  sage: '#3D7A52',
  sageLight: '#6AAE80',
  sagePale: '#C8EDD8',
  mint: '#EEF8F2',
  amber: '#C97D08',
  amberSoft: '#FDF4E3',
  cream: '#FAF6EF',
  parchment: '#F4EFE5',
  ink: '#161612',
  charcoal: '#2C2C28',
  stone: '#6B6B65',
  border: '#E2DDD5',
  borderLight: '#EDE9E1',
  white: '#FFFFFF',
  green: '#12B981',
  greenLight: '#D1FAE5',
  red: '#DC2626',
  redLight: '#FEE2E2',
  amberWarn: '#D97706',
  amberWarnLight: '#FEF3C7',
};

const theme = createTheme({
  palette: {
    primary: {
      main: colors.sage,
      dark: colors.forest,
      light: colors.sageLight,
      contrastText: '#fff',
    },
    secondary: {
      main: colors.amber,
      dark: '#a36506',
      light: colors.amberSoft,
      contrastText: '#fff',
    },
    success: {
      main: colors.green,
      light: colors.greenLight,
    },
    error: {
      main: colors.red,
      light: colors.redLight,
    },
    warning: {
      main: colors.amberWarn,
      light: colors.amberWarnLight,
    },
    background: {
      default: colors.cream,
      paper: '#ffffff',
    },
    text: {
      primary: colors.ink,
      secondary: colors.stone,
    },
    divider: colors.border,
  },
  typography: {
    fontFamily: "'Outfit', sans-serif",
    h1: {
      fontFamily: "'Cormorant Garamond', serif",
      fontWeight: 600,
      lineHeight: 1.08,
      letterSpacing: '-0.025em',
      fontSize: 'clamp(36px, 4.5vw, 60px)',
    },
    h2: {
      fontFamily: "'Cormorant Garamond', serif",
      fontWeight: 500,
      lineHeight: 1.15,
      letterSpacing: '-0.01em',
      fontSize: 'clamp(24px, 2.4vw, 36px)',
    },
    h3: {
      fontFamily: "'Cormorant Garamond', serif",
      fontWeight: 600,
      fontSize: 'clamp(20px, 2.2vw, 28px)',
      letterSpacing: '-0.01em',
      lineHeight: 1.2,
    },
    h4: {
      fontSize: 'clamp(17px, 1.6vw, 20px)',
      fontWeight: 700,
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
    },
    h5: {
      fontSize: '15px',
      fontWeight: 700,
    },
    body1: {
      fontSize: '15px',
      lineHeight: 1.68,
      color: colors.stone,
    },
    body2: {
      fontSize: '13px',
      lineHeight: 1.6,
      color: colors.stone,
    },
    button: {
      fontWeight: 700,
      textTransform: 'none',
      letterSpacing: 0,
    },
    caption: {
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.22em',
      textTransform: 'uppercase',
      color: colors.sage,
    },
  },
  shape: {
    borderRadius: 14,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,600&family=DM+Sans:wght@300;400;500;600;700&family=Outfit:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500&family=Playfair+Display:wght@400;700&family=Space+Mono:wght@400;700&display=swap');

        html { scroll-behavior: smooth; }
        body {
          background: ${colors.cream};
          color: ${colors.ink};
          font-family: 'Outfit', sans-serif;
          font-weight: 400;
          -webkit-font-smoothing: antialiased;
        }
        * { box-sizing: border-box; }
        a { text-decoration: none; color: inherit; }

        .serif { font-family: 'Cormorant Garamond', serif; }
        .mono { font-family: 'JetBrains Mono', monospace; }
        .display { font-family: 'Bebas Neue', sans-serif; letter-spacing: 0.04em; }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.6); opacity: 0; }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.2; }
        }
      `,
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 100,
          fontWeight: 700,
          textTransform: 'none',
          boxShadow: 'none',
          transition: 'all 0.25s',
          '&:hover': {
            boxShadow: 'none',
            transform: 'translateY(-1px)',
          },
        },
        sizeSmall: {
          padding: '8px 20px',
          fontSize: '13px',
        },
        sizeMedium: {
          padding: '11px 26px',
          fontSize: '14px',
        },
        sizeLarge: {
          padding: '15px 36px',
          fontSize: '15px',
        },
        containedPrimary: {
          backgroundColor: colors.sage,
          boxShadow: `0 4px 14px rgba(61,122,82,0.25)`,
          '&:hover': {
            backgroundColor: colors.forest,
            transform: 'translateY(-2px)',
            boxShadow: `0 10px 28px rgba(61,122,82,0.35)`,
          },
        },
        containedSecondary: {
          backgroundColor: colors.amber,
          boxShadow: `0 4px 14px rgba(201,125,8,0.25)`,
          '&:hover': {
            backgroundColor: '#a36506',
            transform: 'translateY(-2px)',
          },
        },
        outlined: {
          borderWidth: 1.5,
          borderColor: colors.border,
          color: colors.charcoal,
          '&:hover': {
            borderWidth: 1.5,
            borderColor: colors.sage,
            color: colors.sage,
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          border: `1px solid ${colors.borderLight}`,
          boxShadow: '0 4px 20px rgba(21,42,30,0.06)',
          transition: 'all 0.35s cubic-bezier(0.23,1,0.32,1)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: '0.04em',
          borderRadius: 100,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'medium',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: colors.cream,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.sage,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.sage,
              borderWidth: 1.5,
            },
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.border,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (min-width: 1200px)': {
            maxWidth: 1300,
          },
        },
      },
    },
    // Stops MUI from adding `padding-right` to <body> whenever a Menu/Select/
    // Autocomplete dropdown opens. That padding causes the navbar + page
    // contents to visibly shift right by the scrollbar width.
    MuiPopover: { defaultProps: { disableScrollLock: true } },
    MuiMenu: { defaultProps: { disableScrollLock: true } },
  },
});

export default theme;
